import { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
	getPgRecurringRunsRequest,
	refreshPgRecurringRunsRequest,
} from 'redux/reducers/pgSettings/reducer';
import {
	selectPgRecurringRuns,
	selectPgRecurringRunsLoading,
} from 'redux/reducers/pgSettings/selectors';
import { TPgListRequest, TPgRecurringRun } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import {
	ESortDirection,
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
	TTableStructureColors,
	TTableStructureFilter,
} from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { usePgListLoader } from '../common/usePgListLoader';
import { PG_RUN_KIND_LABELS, PG_RUN_STATUS_LABELS } from '../constants';
import { RunDetailsDialog } from './RunDetailsDialog';
import { summarizeStats } from './RunStats';

/** `recurring_runs.id` is a GUID, the table needs a numeric `id`: rows get their position instead. */
type TRunRow = Omit<TPgRecurringRun, 'id'> & { id: number; runId: string };

const DETAILS_ACTION = 'Details';

const toFilter = (labels: Record<string, string>): TTableStructureFilter =>
	Object.entries(labels).map(([value, label]) => [value, label]);

const KIND_FILTER = toFilter(PG_RUN_KIND_LABELS);
const STATUS_FILTER = toFilter(PG_RUN_STATUS_LABELS);
const DRY_RUN_FILTER: TTableStructureFilter = [
	['true', 'Dry run'],
	['false', 'Real'],
];
const STATUS_COLORS: TTableStructureColors = [
	['running', 'info.light'],
	['completed', 'success.light'],
	['failed', 'error.light'],
];
const DRY_RUN_COLORS: TTableStructureColors = [
	['true', 'warning.light'],
	['false', 'success.light'],
];

const TABLE_STRUCTURE: TTableStructure<keyof TRunRow, TRunRow>[] = [
	{
		name: 'Run date',
		selector: 'runDate',
		show: true,
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Kind',
		selector: 'kind',
		show: true,
		sortable: true,
		filterable: true,
		filter: KIND_FILTER,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Status',
		selector: 'status',
		show: true,
		sortable: true,
		filterable: true,
		filter: STATUS_FILTER,
		colors: STATUS_COLORS,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Mode',
		selector: 'dryRun',
		show: true,
		sortable: true,
		filterable: true,
		filter: DRY_RUN_FILTER,
		colors: DRY_RUN_COLORS,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
		value: (item) => String(item.dryRun),
	},
	{
		name: 'Triggered by',
		selector: 'triggeredBy',
		show: true,
		sortable: true,
		searchable: true,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Customers',
		selector: 'customersDone',
		show: true,
		sortable: false,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
		value: (item) => `${item.customersDone} of ${item.customersTotal}`,
	},
	{
		name: 'Counters',
		selector: 'stats',
		show: true,
		sortable: false,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
		value: (item) => summarizeStats(item.stats),
	},
	{
		name: 'Started',
		selector: 'startedAt',
		show: true,
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
		type: ETableStructureType.DATE_TIME,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Finished',
		selector: 'finishedAt',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.DATE_TIME,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Actions',
		selector: 'actions',
		noName: true,
		show: true,
		sortable: false,
		filterable: false,
		type: ETableStructureType.BUTTONS,
		align: ETableStructureAlign.RIGHT,
		buttons: [{ name: DETAILS_ACTION, icon: EIconButtons.SUMMARIZE }],
	},
];

/** Runs of the monthly cycle (03 §4.6): newest first, counters, details with live progress. */
export const RunsList: FC = () => {
	const dispatch = useDispatch();
	const runs = useSelector(selectPgRecurringRuns);
	const loading = useSelector(selectPgRecurringRunsLoading);
	const [detailsRunId, setDetailsRunId] = useState<string | null>(null);
	const [tableStructure, setTableStructure] = useState(TABLE_STRUCTURE);

	const load = useCallback(
		(request: TPgListRequest) => dispatch(getPgRecurringRunsRequest(request)),
		[dispatch],
	);
	usePgListLoader(TABLE_STRUCTURE, load, {
		defaultOrder: { fieldName: 'startedAt', direction: ESortDirection.DESC },
	});

	const rows = useMemo(
		() =>
			runs && {
				...runs,
				data: runs.data.map(
					({ id, ...run }, index): TRunRow => ({ ...run, id: index + 1, runId: id }),
				),
			},
		[runs],
	);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action !== DETAILS_ACTION) return;
			setDetailsRunId(rows?.data.find((row) => row.id === id)?.runId ?? null);
		},
		[rows],
	);

	return (
		<>
			<EasTableWithFilter<TRunRow>
				tableStructure={tableStructure}
				items={rows}
				itemsIsLoading={loading}
				handleAction={handleAction}
				onTableStructureChange={setTableStructure}
				skeletonRows={Array.from(new Array(5))}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Runs
					</Typography>
					<Button
						size="small"
						onClick={() => dispatch(refreshPgRecurringRunsRequest())}
						disabled={loading}
						startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
					>
						Refresh
					</Button>
				</Box>
			</EasTableWithFilter>
			<RunDetailsDialog runId={detailsRunId} onClose={() => setDetailsRunId(null)} />
		</>
	);
};
